import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { API_BASE_URL, API_ENDPOINTS, STORAGE_KEYS } from '../../constants';
import { loginFailure, updateToken, updateUser } from '../slices/authSlice';
import { shouldRefreshSessionOnHttpError } from './apiReauthPolicy';
// RootState currently not used here; keep import for future enhancements
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { RootState } from '../../store';

// Безопасное получение токена
const getTokenFromStorage = (): string | null => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    return token && token !== 'undefined' && token !== 'null' ? token : null;
  } catch {
    return null;
  }
};

const redirectToLoginSafely = () => {
  try {
    const currentPath = window.location.pathname;
    if (currentPath === '/login') return;
    window.location.replace('/login');
  } catch {
    window.location.href = '/login';
  }
};

// Base query with auth token injection
const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = getTokenFromStorage();
    // Debug logs removed for production; keep minimal check
    // console.debug('🔑 Preparing headers, token:', token ? 'exists' : 'missing');
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    
    // Не устанавливаем content-type для FormData запросов
    // Браузер сам установит правильный content-type с boundary для FormData
    // Устанавливаем только для JSON запросов
    // headers.set('content-type', 'application/json');
    
    return headers;
  },
});

let refreshPromise: Promise<string | null> | null = null;

function isAuthRequest(args: string | FetchArgs): boolean {
  const url = typeof args === 'string' ? args : String((args as FetchArgs)?.url || '');
  return url.includes('/auth/');
}

function clearAuthSession(api: any) {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
  api.dispatch(loginFailure());
}

/** После refresh access-токена подтянуть пользователя из БД (актуальные permissions). */
async function syncUserProfileAfterRefresh(api: any, extraOptions: any) {
  try {
    const prof = await baseQuery({ url: API_ENDPOINTS.AUTH.PROFILE, method: 'GET' }, api, extraOptions);
    if (prof && 'data' in prof && prof.data) {
      const outer = prof.data as { data?: unknown };
      const user = outer && typeof outer === 'object' && 'data' in outer ? (outer as { data: unknown }).data : outer;
      if (user && typeof user === 'object' && user !== null && 'id' in user) {
        api.dispatch(updateUser(user as any));
      }
    }
  } catch {
    /* ignore */
  }
}

async function refreshAccessToken(api: any, extraOptions: any): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken || refreshToken === 'undefined' || refreshToken === 'null') {
      clearAuthSession(api);
      return null;
    }

    const refreshResult = await baseQuery(
      {
        url: API_ENDPOINTS.AUTH.REFRESH,
        method: 'POST',
        body: { refreshToken },
      },
      api,
      extraOptions
    );

    if (!refreshResult.data) {
      clearAuthSession(api);
      return null;
    }

    const payload = (refreshResult.data as { data?: { accessToken?: string } })?.data ?? refreshResult.data as { accessToken?: string };
    const accessToken = payload?.accessToken ?? (refreshResult.data as any)?.accessToken;
    if (!accessToken) {
      clearAuthSession(api);
      return null;
    }

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    api.dispatch(updateToken(accessToken));
    await syncUserProfileAfterRefresh(api, extraOptions);
    return accessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

// Base query with token refresh
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // console.debug('🌐 Making API request:', args);
  let result = await baseQuery(args, api, extraOptions);
  // console.debug('📡 API response:', result);

  // 401 — неавторизован / PERMISSIONS_STALE: refresh access token and retry once
  if (
    result.error &&
    typeof result.error.status === 'number' &&
    shouldRefreshSessionOnHttpError(result.error.status)
  ) {
    if (!isAuthRequest(args)) {
      const accessToken = await refreshAccessToken(api, extraOptions);
      if (accessToken) {
        result = await baseQuery(args, api, extraOptions);
      } else {
        redirectToLoginSafely();
      }
    }
  }

  // 403 Forbidden — valid session, insufficient rights; do not refresh or logout

  // Обработка 429 (Too Many Requests) - слишком много запросов
  if (result.error && result.error.status === 429) {
    console.log('Rate limit exceeded (429). Too many requests to the server.');
    // Не делаем повторных попыток для 429 ошибок, чтобы не усугублять проблему
    return result;
  }

  // Обработка ошибок сети или сервера
  if (result.error && !result.error.status) {
    console.error('Network error or server unavailable:', result.error);
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Auth',
    'Application',
    'Route',
    'Cargo',
    'CargoHistory',
    'Audit',
    'Warehouse',
    'Document',
    'Notification',
    'NotificationSettings',
    'User',
    'Client',
    'Expeditor',
    'Shipper',
    'Consignee',
    'Driver',
    'Vehicle',
    'Carrier',
    'Report',
    'Contractor',
    'RailwayStation',
    'Airport',
    'Location',
    'RouteLocation',
    'TN',
    'Search',
    'Settings',
    'Workspace',
    'ApplicationControlTasks',
    'RouteTaskHistory',
    'ApplicationTaskHistory',
    'CarrierAgreement',
    'Contract',
    'RoleCatalog',
    'ClosingPackage',
  ],
  endpoints: () => ({}),
});

export { baseQuery };
export default apiSlice; 